import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Compass,
  Image,
  Megaphone,
  MessageSquareText,
  PenTool,
  ShieldCheck,
  Sparkles,
  Star,
  Workflow,
} from 'lucide-react';
import { publicApi } from '../utils/api';
import StackConsultingPrime from '../components/StackConsultingPrime';
import SEOHead from '../components/SEOHead';

const getGuidePresentation = (label = '') => {
  const normalized = String(label || '').toLowerCase();
  if (normalized.includes('writing') || normalized.includes('content') || normalized.includes('copy')) {
    return {
      icon: PenTool,
      accent: '#ffe86b',
      surface: 'linear-gradient(135deg, rgba(255,232,107,0.18), rgba(255,232,107,0.03))',
    };
  }
  if (normalized.includes('image') || normalized.includes('video') || normalized.includes('design')) {
    return {
      icon: Image,
      accent: '#7ef3be',
      surface: 'linear-gradient(135deg, rgba(126,243,190,0.18), rgba(126,243,190,0.03))',
    };
  }
  if (normalized.includes('seo') || normalized.includes('marketing') || normalized.includes('sales')) {
    return {
      icon: Megaphone,
      accent: '#ff9d57',
      surface: 'linear-gradient(135deg, rgba(255,157,87,0.18), rgba(255,157,87,0.03))',
    };
  }
  if (normalized.includes('support') || normalized.includes('chat') || normalized.includes('customer')) {
    return {
      icon: MessageSquareText,
      accent: '#7bc5ff',
      surface: 'linear-gradient(135deg, rgba(123,197,255,0.18), rgba(123,197,255,0.03))',
    };
  }
  return {
    icon: Workflow,
    accent: '#d39dff',
    surface: 'linear-gradient(135deg, rgba(211,157,255,0.18), rgba(211,157,255,0.03))',
  };
};

const humanize = (value = '') =>
  String(value || '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const sanitizeCopy = (value = '') =>
  String(value || '')
    .replace(/Ã¢/g, '-')
    .replace(/[""]/g, '"')
    .replace(/[']/g, "'")
    .replace(/\b([A-Za-z]{3,})(\s+\1\b)+/gi, '$1')
    .replace(/\s+/g, ' ')
    .trim();

const getSourceHost = (value = '') => {
  try {
    return new URL(value).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const buildDisplayTitle = (page = {}) => {
  const category = humanize(page.category_label || 'AI Tools');
  const role = humanize(page.role || page.audience || 'buyers');
  const useCase = humanize(page.use_case || '');
  const modifier = page.modifier === 'free'
    ? 'Free buyer brief'
    : page.modifier === 'under-50-per-month'
      ? 'Budget buyer brief'
      : page.modifier === 'soc2'
        ? 'Enterprise-ready buyer brief'
        : 'Buyer brief';

  if (useCase && page.role) {
    return `${category} for ${role}: ${useCase}`;
  }
  if (useCase && useCase.toLowerCase() !== category.toLowerCase()) {
    return `${category}: ${useCase}`;
  }
  if (page.role) {
    return `${category} for ${role}`;
  }
  return `${modifier} for ${category}`;
};

const buildCategoryLink = (label = '') => `/categories/${String(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

const BestUseCasePage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await publicApi.getBestPageBySlug(slug);
        if (!active) {
          return;
        }
        setPage(res.data);
      } catch {
        if (!active) {
          return;
        }
        setError('This best tools page is not available yet.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [slug]);

  const lastVerified = useMemo(() => {
    const value = page?.updated_at || page?.date_modified || page?.created_at;
    if (!value) {
      return 'Recently';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? 'Recently'
      : date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  }, [page?.updated_at, page?.date_modified, page?.created_at]);

  const guideTheme = useMemo(
    () => getGuidePresentation(page?.category_label || page?.use_case || page?.h1),
    [page?.category_label, page?.use_case, page?.h1]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-void pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-yellow" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-void pt-24">
        <div className="container mx-auto px-6 py-16 max-w-3xl">
          <h1 className="text-3xl text-white font-black mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Best AI Tools
          </h1>
          <p className="text-text-secondary mb-6">{error || 'Page unavailable.'}</p>
          <Link to="/best?sort=overall" className="text-cyber-yellow hover:underline">
            Back to rankings
          </Link>
        </div>
      </div>
    );
  }

  const GuideIcon = guideTheme.icon;
  const topRows = page.table_rows || [];
  const topCards = page.tool_cards || [];
  const detailContent = page.detail_content || {};
  const displayTitle = buildDisplayTitle(page);
  const displayIntro = sanitizeCopy(detailContent.executive_summary || page.intro);
  const documentSignals = [
    { label: 'Audience', value: humanize(page.role || page.audience || 'buyers') },
    { label: 'Scope', value: humanize(page.use_case || page.category_label || 'general evaluation') },
    { label: 'Evidence', value: (detailContent.live_sources || []).length ? 'Live vendor pages + catalog data' : 'Catalog data + structured summaries' },
  ];
  const stats = [
    { label: 'Tools shortlisted', value: `${topRows.length}`, icon: Compass },
    { label: 'Decision FAQs', value: `${(page.faq || []).length}`, icon: ShieldCheck },
    { label: 'Refresh cadence', value: 'Auto', icon: Sparkles },
    { label: 'Last verified', value: lastVerified, icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-void pt-24" data-testid="best-use-case-page">
      <SEOHead
        pageType="best-of"
        title={page.meta_title || page.h1}
        description={page.meta_description || page.intro || ''}
        slug={`best/${slug}`}
        faqs={page.faq || []}
        comparisons={{ rows: page.table_rows || [] }}
        dateModified={page.updated_at || page.date_modified || page.created_at}
        author={page.author || 'Wowrolla Editorial'}
        category="best-of"
      />

      <section className="container mx-auto px-6 py-8 max-w-6xl">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="flex flex-wrap items-center gap-2 text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
            <Link to="/best?sort=overall" className="hover:text-cyber-yellow transition-colors">Rankings</Link>
            <ChevronRight size={14} />
            <span>{page.category_label || 'Guide'}</span>
            {page.use_case && (
              <>
                <ChevronRight size={14} />
                <span>{humanize(page.use_case)}</span>
              </>
            )}
          </div>

          <div className="rounded-[28px] p-6 lg:p-8" style={{ background: guideTheme.surface, border: `1px solid ${guideTheme.accent}33` }}>
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="max-w-4xl">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-black/25 mb-4">
                  <GuideIcon size={14} style={{ color: guideTheme.accent }} />
                  <span className="text-xs uppercase tracking-[0.18em] font-mono" style={{ color: guideTheme.accent }}>
                    {page.guide_badge || 'Curated guide'}
                  </span>
                </div>
                <p className="text-xs uppercase tracking-[0.18em] font-mono mb-3" style={{ color: guideTheme.accent }}>
                  Enterprise buyer document
                </p>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {displayTitle}
                </h1>
                <p className="text-base sm:text-lg text-text-secondary max-w-3xl">{displayIntro}</p>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-4xl">
                  {documentSignals.map((item) => (
                    <div key={item.label} className="rounded-2xl p-4" style={{ background: 'rgba(0,0,0,0.16)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div className="text-[10px] uppercase tracking-[0.18em] font-mono mb-2" style={{ color: guideTheme.accent }}>{item.label}</div>
                      <div className="text-sm text-white leading-6">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 min-w-[280px]">
                {stats.map((stat) => {
                  const StatIcon = stat.icon;
                  return (
                    <div key={stat.label} className="rounded-2xl p-4" style={{ background: 'rgba(0,0,0,0.18)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-mono mb-2" style={{ color: guideTheme.accent }}>
                        <StatIcon size={12} /> {stat.label}
                      </div>
                      <div className="text-xl font-black text-white">{stat.value}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/best?sort=overall" className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm" style={{ background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.08)' }}>
                Browse rankings <ArrowRight size={13} />
              </Link>
              {page.category_label && (
                <Link to={buildCategoryLink(page.category_label)} className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm" style={{ background: `${guideTheme.accent}18`, color: guideTheme.accent, border: `1px solid ${guideTheme.accent}33` }}>
                  Open category page <ArrowRight size={13} />
                </Link>
              )}
            </div>
          </div>
        </motion.div>
      </section>

      <section className="container mx-auto px-6 pb-8 max-w-6xl">
        <div className="grid grid-cols-1 xl:grid-cols-[1.25fr_minmax(0,0.95fr)] gap-6">
          <div className="terminal-grid p-6">
            <div className="flex items-center gap-2 mb-5">
              <Compass size={18} style={{ color: guideTheme.accent }} />
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Shortlist at a glance
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
              {topRows.slice(0, 3).map((row, index) => (
                <Link
                  key={row.tool_id || row.slug}
                  to={`/tools/${row.slug}`}
                  className="rounded-2xl p-5 block transition-transform hover:-translate-y-0.5"
                  style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${guideTheme.accent}26` }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.18em]" style={{ color: guideTheme.accent, background: `${guideTheme.accent}1a` }}>
                      Pick #{index + 1}
                    </span>
                    <span className="text-xs text-text-muted">{row.price || 'Pricing varies'}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{row.name}</h3>
                  <p className="text-sm text-text-secondary line-clamp-3">{row.best_for || 'Strong fit for this guide.'}</p>
                </Link>
              ))}
            </div>

            <div className="overflow-x-auto rounded-2xl" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
              <table className="w-full text-sm">
                <thead style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <tr className="text-left text-text-muted">
                    <th className="py-3 px-4">Tool</th>
                    <th className="py-3 px-4">Best for</th>
                    <th className="py-3 px-4">Price</th>
                    <th className="py-3 px-4">Disclosure</th>
                  </tr>
                </thead>
                <tbody>
                  {topRows.map((row) => (
                    <tr key={row.tool_id || row.slug} className="border-t border-white/5">
                      <td className="py-3 px-4 text-white font-medium">
                        <Link to={`/tools/${row.slug}`} className="hover:text-cyber-yellow transition-colors">{row.name}</Link>
                      </td>
                      <td className="py-3 px-4 text-text-secondary">{row.best_for}</td>
                      <td className="py-3 px-4 text-text-secondary">{row.price}</td>
                      <td className="py-3 px-4 text-text-secondary">{row.commission_note || 'Disclosed'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="terminal-grid p-6">
            <div className="flex items-center gap-2 mb-5">
              <Sparkles size={18} style={{ color: guideTheme.accent }} />
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Executive brief
              </h2>
            </div>
            <p className="text-text-secondary mb-5">{displayIntro}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              {[
                'Workflow fit before feature breadth',
                'Governance and rollout risk',
                'Visible pricing and plan structure',
              ].map((item) => (
                <div key={item} className="rounded-2xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <p className="text-xs text-white leading-5">{item}</p>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              {(detailContent.buyer_checklist || [
                'Validate security and admin controls before scaling procurement.',
                'Compare onboarding friction and integration fit with your current stack.',
                'Review all visible pricing tiers before piloting.',
                'Use a real workflow and measurable success criteria in the pilot.',
              ]).map((item, index) => {
                const checklistItems = [Workflow, CircleDollarSign, ShieldCheck, BarChart3];
                const ItemIcon = checklistItems[index % checklistItems.length];
                return (
                  <div key={`${item}-${index}`} className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="flex items-center gap-2" style={{ color: guideTheme.accent }}>
                      <ItemIcon size={15} />
                      <span className="text-sm text-white">{sanitizeCopy(item)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 rounded-2xl p-4" style={{ background: `${guideTheme.accent}12`, border: `1px solid ${guideTheme.accent}26` }}>
              <div className="text-xs font-mono uppercase tracking-[0.18em] mb-2" style={{ color: guideTheme.accent }}>Next step</div>
              <Link to="/find" className="inline-flex items-center text-white hover:text-cyber-yellow transition-colors">
                <CheckCircle2 size={18} className="mr-2" />
                {page.cta || "Not sure which fits your setup? Use Wowrolla's AI finder ->"}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 pb-8 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_minmax(0,0.95fr)] gap-6">
          <div className="terminal-grid p-6">
            <div className="flex items-center gap-2 mb-5">
              <Workflow size={18} style={{ color: guideTheme.accent }} />
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Implementation plan
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(detailContent.implementation_plan || [
                'Pick a pilot workflow and define baseline metrics.',
                'Run two shortlisted tools against the same use case.',
                'Validate security, SSO, and integration requirements.',
                'Roll out with ownership, enablement, and pricing review.',
              ]).map((step, index) => (
                <div key={`${step}-${index}`} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${guideTheme.accent}22` }}>
                  <div className="text-xs font-mono uppercase tracking-[0.18em] mb-2" style={{ color: guideTheme.accent }}>Step {index + 1}</div>
                  <p className="text-sm text-text-secondary">{sanitizeCopy(step)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="terminal-grid p-6">
            <div className="flex items-center gap-2 mb-5">
              <Compass size={18} style={{ color: guideTheme.accent }} />
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Example enterprise uses
              </h2>
            </div>
            <div className="space-y-4">
              {(detailContent.use_case_examples || []).map((example, index) => (
                <div key={`${example.title}-${index}`} className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="text-xs uppercase tracking-[0.18em] font-mono mb-2" style={{ color: guideTheme.accent }}>Use case {index + 1}</div>
                  <div className="text-sm font-semibold text-white mb-2">{sanitizeCopy(example.title)}</div>
                  <p className="text-sm text-text-secondary">{sanitizeCopy(example.detail)}</p>
                </div>
              ))}
              {(!detailContent.use_case_examples || detailContent.use_case_examples.length === 0) && (
                <p className="text-sm text-text-secondary">Enterprise examples will appear here as the guide is enriched.</p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 pb-8 max-w-6xl">
        <div className="terminal-grid p-6">
          <div className="flex items-center gap-2 mb-5">
            <CircleDollarSign size={18} style={{ color: guideTheme.accent }} />
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Pricing plans and enterprise signals
            </h2>
          </div>
          <div className="space-y-4">
            {(detailContent.pricing_matrix || []).map((item) => (
              <div key={item.tool_name} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] font-mono mb-2" style={{ color: guideTheme.accent }}>Commercial view</div>
                    <h3 className="text-lg font-semibold text-white mb-1">{sanitizeCopy(item.tool_name)}</h3>
                    <div className="flex flex-wrap gap-2">
                      {(item.enterprise_signals || []).map((signal) => (
                        <span key={signal} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-mono uppercase tracking-[0.18em]" style={{ color: guideTheme.accent, background: `${guideTheme.accent}16` }}>
                          {sanitizeCopy(signal)}
                        </span>
                      ))}
                      {(!item.enterprise_signals || item.enterprise_signals.length === 0) && (
                        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-mono uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)', background: 'rgba(255,255,255,0.05)' }}>
                          No enterprise signal surfaced
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {(item.plans || []).map((plan, index) => (
                    <div key={`${item.tool_name}-${plan.name}-${index}`} className="rounded-xl p-4" style={{ background: 'rgba(0,0,0,0.16)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <div className="text-sm font-semibold text-white mb-1">{sanitizeCopy(plan.name)}</div>
                      <div className="text-sm mb-3" style={{ color: guideTheme.accent }}>{sanitizeCopy(plan.price)}</div>
                      <div className="space-y-1">
                        {(plan.features || []).map((feature) => (
                          <div key={feature} className="text-xs text-text-secondary">- {sanitizeCopy(feature)}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {(detailContent.enterprise_notes || []).length > 0 && (
        <section className="container mx-auto px-6 pb-8 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,1.1fr)] gap-6">
            <div className="terminal-grid p-6">
              <div className="flex items-center gap-2 mb-5">
                <ShieldCheck size={18} style={{ color: guideTheme.accent }} />
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  Enterprise notes
                </h2>
              </div>
              <div className="space-y-3">
                {detailContent.enterprise_notes.map((note, index) => (
                  <div key={`${note}-${index}`} className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="text-xs uppercase tracking-[0.18em] font-mono mb-2" style={{ color: guideTheme.accent }}>Observed signal {index + 1}</div>
                    <p className="text-sm text-text-secondary">{sanitizeCopy(note)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="terminal-grid p-6">
              <div className="flex items-center gap-2 mb-5">
                <BarChart3 size={18} style={{ color: guideTheme.accent }} />
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  Live source references
                </h2>
              </div>
              <div className="space-y-4">
                {(detailContent.live_sources || []).map((source) => (
                  <a
                    key={`${source.tool_name}-${source.source_url}`}
                    href={source.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="block rounded-2xl p-4 transition-colors hover:bg-white/5"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className="text-sm font-semibold text-white mb-1">{sanitizeCopy(source.tool_name)}</div>
                        <div className="text-[11px] font-mono uppercase tracking-[0.18em]" style={{ color: guideTheme.accent }}>{getSourceHost(source.source_url) || 'Vendor source'}</div>
                      </div>
                      {source.fetched_at && (
                        <div className="text-[11px] text-text-muted whitespace-nowrap">{new Date(source.fetched_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                      )}
                    </div>
                    <div className="text-xs mb-2" style={{ color: guideTheme.accent }}>{sanitizeCopy(source.title)}</div>
                    <p className="text-sm text-text-secondary line-clamp-4">{sanitizeCopy(source.excerpt)}</p>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="container mx-auto px-6 pb-8 max-w-6xl">
        <div className="flex items-center gap-2 mb-5">
          <Star size={18} style={{ color: guideTheme.accent }} />
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Tool snapshots
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {topCards.map((card, index) => (
            <div key={card.tool_id || card.slug} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${index % 2 === 0 ? `${guideTheme.accent}28` : 'rgba(255,255,255,0.08)'}` }}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-lg text-white font-semibold mb-1">{card.name}</h3>
                  <p className="text-sm" style={{ color: guideTheme.accent }}>{sanitizeCopy(card.tagline || 'Buyer snapshot')}</p>
                </div>
                <span className="px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.18em]" style={{ color: guideTheme.accent, background: `${guideTheme.accent}18` }}>
                  {page.guide_badge || 'Guide fit'}
                </span>
              </div>
              <p className="text-text-secondary mb-4">{sanitizeCopy(card.summary)}</p>
              <Link to={`/tools/${card.slug}`} className="inline-flex items-center gap-1 hover:underline" style={{ color: guideTheme.accent }}>
                View tool details <ArrowRight size={14} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {(page.faq || []).length > 0 && (
        <section className="container mx-auto px-6 pb-8 max-w-6xl">
          <div className="flex items-center gap-2 mb-5">
            <ShieldCheck size={18} style={{ color: guideTheme.accent }} />
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Decision FAQs
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {page.faq.map((item, index) => (
              <div key={`${item.q}-${index}`} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <h3 className="text-base text-white font-semibold mb-2">{item.q}</h3>
                <p className="text-sm text-text-secondary">{item.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {(page.related_pages || []).length > 0 && (
        <section className="container mx-auto px-6 pb-12 max-w-6xl">
          <div className="flex items-center gap-2 mb-5">
            <Compass size={18} style={{ color: guideTheme.accent }} />
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Related guides
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {page.related_pages.map((related, index) => (
              <Link key={related.slug} to={`/best/${related.slug}`} className="rounded-2xl p-5 block transition-transform hover:-translate-y-0.5" style={{ background: index % 2 === 0 ? guideTheme.surface : 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="text-[11px] uppercase tracking-[0.18em] font-mono mb-2" style={{ color: guideTheme.accent }}>Related guide</div>
                <h3 className="text-base text-white font-semibold line-clamp-2 mb-2">{related.title}</h3>
                <p className="text-sm text-text-secondary line-clamp-3">{related.meta_description}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="container mx-auto px-6 pb-20 max-w-6xl">
        <StackConsultingPrime />
      </section>
    </div>
  );
};

export default BestUseCasePage;

